'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import {
  LogoLinkedin,
  LogoGithub,
  Email,
  LogoTwitter,
  Copy,
  Checkmark,
} from '@carbon/icons-react';
import { Tag } from '@carbon/react';
import TableOfContents from '@/components/TableOfContent/TableOfContents';
import BottomBanner from '@/components/BannerSection/BottomBanner/BottomBanner';
import CarbonBreadcrumb from '@/components/BreadCrumb/BreadCrumbComponent';
import { getChampionByExternalId } from '@/apis/ChampionsAPIs';
import { formatProfileImageUrl } from '@/utils/urls';
import type { Champion } from '@/app/types/profile';

import './champion-profile.scss';
interface Props {
  params: { username: string };
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="champion-profile-copy-btn"
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? <Checkmark size={16} style={{ color: '#24a148' }} /> : <Copy size={16} />}
    </button>
  );
}

const CONTACT_LABEL: Record<string, string> = {
  EMAIL: 'Email',
  LINKEDIN: 'LinkedIn',
  GITHUB: 'GitHub',
};

export default function ChampionProfilePage({ params }: Props) {
  const [champion, setChampion] = useState<Champion | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState<string>('');

  useEffect(() => {
    getChampionByExternalId(params.username).then(data => {
      setChampion(data);
      setLoading(false);
    });
  }, [params.username]);

  useEffect(() => {
    if (!champion) return;
    let interval: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;

    const resolveTimeZone = async (): Promise<string | undefined> => {
      const explicit = (champion as any).timezone || (champion as any).timezone_iana_zone;
      if (explicit) return explicit;

      const city = champion.city?.trim();
      const country = champion.country?.trim().toLowerCase();
      if (!city && !country) return undefined;

      const { lookupViaCity, findFromIsoCode, findFromCityStateProvince } =
        await import('city-timezones');
      type CityData = ReturnType<typeof lookupViaCity>[number];
      const matchesCountry = (c: CityData) =>
        !country || [c.country, c.iso2, c.iso3].some(v => String(v).toLowerCase() === country);
      const mostPopulous = (list: CityData[]) =>
        list.length ? list.reduce((a, b) => (b.pop > a.pop ? b : a)) : undefined;

      const cityMatches: CityData[] = city ? lookupViaCity(city) : [];
      const inCountry = cityMatches.filter(matchesCountry);
      if (inCountry.length) return mostPopulous(inCountry)?.timezone;
      if (cityMatches.length) return mostPopulous(cityMatches)?.timezone;
      if (!country) return undefined;

      const countryMatches = findFromIsoCode(country);
      return mostPopulous(
        countryMatches.length
          ? countryMatches
          : findFromCityStateProvince(champion.country).filter(matchesCountry),
      )?.timezone;
    };

    resolveTimeZone()
      .catch(err => {
        console.error('Error resolving timezone:', err);
        return undefined;
      })
      .then(timeZone => {
        if (cancelled || !timeZone) return;
        const updateTime = () => {
          try {
            const now = new Date();
            const time12 = now.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
              timeZone,
            });
            const time24 = now.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: false,
              timeZone,
            });
            setCurrentTime(`${time12} | ${time24}`);
          } catch (err) {
            console.error('Error formatting time:', err);
          }
        };
        updateTime();
        interval = setInterval(updateTime, 60000);
      });

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [champion]);

  if (loading) return <div className="champion-profile-page">Loading...</div>;
  if (!champion) return null;

  const name = `${champion.first_name ?? ''} ${champion.last_name ?? ''}`.trim();
  const location = [champion.city, champion.country].filter(Boolean).join(', ');
  const preferredLabel =
    (champion.preferred_contact_method && CONTACT_LABEL[champion.preferred_contact_method]) ??
    champion.preferred_contact_method;

  const bioParagraphs = champion.bio?.split('\n').filter(Boolean) || [];
  const relationship = (champion as any).relationship || (champion as any).relationship_to_ibm;
  const advocacyActivities =
    (champion as any).advocacy_activities ||
    (champion as any).preferred_advocacy_activities;
  const hasAdvocacyActivities =
    Array.isArray(advocacyActivities) && advocacyActivities.length > 0;
  const hasSkills = (champion.skills ?? []).length > 0;

  const hasContactInfo = Boolean(
    preferredLabel ||
      (champion as any).important_contact_info ||
      (champion as any).availability ||
      champion.email ||
      champion.linkedin ||
      champion.github
  );

  const navigationItems = [
    { label: 'Profile', href: '#profile' },
    ...(bioParagraphs.length > 0 ? [{ label: 'Bio', href: '#bio' }] : []),
    ...(hasContactInfo ? [{ label: 'Contact', href: '#contact' }] : []),
    ...(relationship ? [{ label: 'Relationship to IBM', href: '#relationship-to-ibm' }] : []),
    ...(hasSkills || hasAdvocacyActivities
      ? [{ label: 'Area of expertise', href: '#area-of-expertise' }]
      : []),
  ];

  const getGithubUsername = (url: string) => {
    if (!url) return '';
    const cleanUrl = url.replace(/\/$/, '');
    const parts = cleanUrl.split('/');
    return `@${parts[parts.length - 1]}`;
  };

  return (
    <div className="champion-profile-page">
      {/* Main layout: sidebar + content */}
      <div className="champion-profile-layout">
        {/* Sidebar TOC */}
        <aside className="champion-profile-sidebar">
          <TableOfContents
            navigationItems={navigationItems}
            hideTitle={true}
            defaultActiveFirst={true}
          />
        </aside>

        {/* Content */}
        <main className="champion-profile-content">
          {/* Breadcrumb — full width, above the grid */}
          <CarbonBreadcrumb
            items={[
              { label: 'Meet the champions', href: '/community/ibm-champions/' },
              { label: name, isCurrentPage: true },
            ]}
          />

          {/* Profile header */}
          <section id="profile" className="champion-profile-header">
            <div className="champion-profile-header__inner">
              <div className="champion-profile-header__avatar">
                {champion.profile_image ? (
                  <Image
                    src={formatProfileImageUrl(champion.profile_image)}
                    alt={name}
                    width={120}
                    height={120}
                    className="champion-profile-header__img"
                    unoptimized
                  />
                ) : (
                  <div className="champion-profile-header__img" />
                )}
              </div>

              <div className="champion-profile-header__meta">
                <div className="champion-profile-header__name-row">
                  <h1 className="champion-profile-header__name">{name}</h1>
                </div>

                {champion.role && <p className="champion-profile-header__role">{champion.role}</p>}
                <p className="champion-profile-header__location">
                  <strong>{location}</strong>
                  {currentTime && (
                    <span className="champion-profile-header__time">{currentTime}</span>
                  )}
                </p>

                <div className="champion-profile-header__socials">
                  {champion.linkedin && (
                    <a
                      href={champion.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                    >
                      <LogoLinkedin size={20} />
                    </a>
                  )}
                  {((champion as any).twitter || (champion as any).twitter_id) && (
                    <a
                      href={(champion as any).twitter || (champion as any).twitter_id}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Twitter"
                    >
                      <LogoTwitter size={20} />
                    </a>
                  )}
                  {champion.github && (
                    <a
                      href={champion.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="GitHub"
                    >
                      <LogoGithub size={20} />
                    </a>
                  )}
                  {champion.email && (
                    <a href={`mailto:${champion.email}`} aria-label="Email">
                      <Email size={20} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Bio */}
          {bioParagraphs.length > 0 && (
            <>
              <hr className="champion-profile-divider" />
              <section id="bio" className="champion-profile-section">
                <h2>Bio</h2>
                <div className="champion-profile-bio">
                  <p className="champion-profile-bio__label">Experience</p>
                  {bioParagraphs.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </section>
            </>
          )}

          {/* Contact */}
          {hasContactInfo && (
            <>
              <hr className="champion-profile-divider" />
              <section id="contact" className="champion-profile-section">
                <h2>Contact</h2>
                <table className="champion-profile-table">
                  <tbody>
                    {preferredLabel && (
                      <tr>
                        <td className="champion-profile-table__label">Preferred contact:</td>
                        <td>{preferredLabel}</td>
                      </tr>
                    )}
                    {((champion as any).important_contact_info ||
                      (champion as any).availability) && (
                      <tr>
                        <td className="champion-profile-table__label">
                          Important contact information:
                        </td>
                        <td>
                          {(champion as any).important_contact_info ||
                            (champion as any).availability}
                        </td>
                      </tr>
                    )}
                    {champion.email && (
                      <tr>
                        <td className="champion-profile-table__label">Email:</td>
                        <td>
                          <div className="champion-profile-table__value-container">
                            <a href={`mailto:${champion.email}`} className="champion-profile-link">
                              {champion.email}
                            </a>
                            <CopyButton text={champion.email} />
                          </div>
                        </td>
                      </tr>
                    )}
                    {champion.linkedin && (
                      <tr>
                        <td className="champion-profile-table__label">LinkedIn:</td>
                        <td>
                          <div className="champion-profile-table__value-container">
                            <a
                              href={champion.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="champion-profile-link"
                            >
                              {champion.linkedin}
                            </a>
                            <CopyButton text={champion.linkedin} />
                          </div>
                        </td>
                      </tr>
                    )}
                    {champion.github && (
                      <tr>
                        <td className="champion-profile-table__label">Github:</td>
                        <td>
                          <div className="champion-profile-table__value-container">
                            <a
                              href={champion.github}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="champion-profile-link"
                            >
                              {getGithubUsername(champion.github)}
                            </a>
                            <CopyButton text={champion.github} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </section>
            </>
          )}

          {relationship && (
            <>
              <hr className="champion-profile-divider" />
              <section id="relationship-to-ibm" className="champion-profile-section">
                <h2>Relationship to IBM</h2>
                <ul className="champion-profile-list">
                  <li>{relationship}</li>
                </ul>
              </section>
            </>
          )}

          {/* Area of expertise */}
          {(hasSkills || hasAdvocacyActivities) && (
            <>
              <hr className="champion-profile-divider" />
              <section id="area-of-expertise" className="champion-profile-section">
                <h2>Area of expertise</h2>
                {hasSkills && (
                  <>
                    <p className="champion-profile-bio__label">Product skills</p>
                    <div className="champion-profile-skills">
                      {champion.skills.map((skill, i) => (
                        <Tag key={i} type="blue" size="md">
                          {skill}
                        </Tag>
                      ))}
                    </div>
                  </>
                )}

                {hasAdvocacyActivities && (
                  <>
                    <p className="champion-profile-bio__label champion-profile-bio__label--spaced">
                      Preferred advocacy activities
                    </p>
                    <ul className="champion-profile-list">
                      {advocacyActivities.map((activity: string, i: number) => (
                        <li key={i}>{activity}</li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            </>
          )}

          <hr className="champion-profile-divider" />
        </main>
      </div>

      <BottomBanner
        title="Get to know the Champions and other advocates"
        subHeader="Join the IBM Champions topic group on this community"
        buttonText="Join the community"
        buttonLink="https://community.ibm.com/community/user/champions?CommunityKey=50275c5e-5c6e-4dc5-ae97-805203da2163"
        backgroundColor="#f4f4f4"
      />
    </div>
  );
}
